import TemplateEditorStore from "../../components/templateEditor/TemplateEditorStore";
import {action, computed, flow, makeObservable, observable} from "mobx";
import {API} from "aws-amplify";
import {getCampaignRetryTemplate, getUrlFile} from "../../../graphql/queries";
import axios from "axios";
import {OriginVariables} from "../../../utils/enums";

class RetryStore {
  retries = {};
  customer = null; 
  campaign = null;
  
  modalStore = null;

  isGetTemplateBody = false;
  isAddRetry = false;

  mappedVariables = [];
  sources = [];
  
  constructor({ 
    campaign,
    customer,
    modalStore 
  }) {    
    makeObservable(this, {
      retries: observable,
      isGetTemplateBody: observable,
      isAddRetry: observable,
      updateRetryAttribute: action,
      add: action,
      remove: action,
      getRetryTemplate: action,
      reOrderRetries: action,
      expandEditor: action,
      saveEditorData: action,
      setVariablesFromFile: action,
      setCampaign: action,
      isLoading: computed,
      retriesCount: computed,
      isMaxRetries: computed,
      retriesArray: computed,
      getEmailBody: flow,
      fetchRetryTemplate: flow,
      fetchEmailMessageBody: flow
    });

    this.modalStore = modalStore
    
    this.campaign = campaign;
    this.customer = customer;

    // set retries correctly
    if (this.campaign.first_message) {
      this.retries = this.campaign.first_message;

      Object.keys(this.retries).forEach((key) => {
        const item = this.retries[key];
        if (item.variables && Array.isArray(item.variables)) {
          item.variables.forEach((variable) => {
            variable.variable = variable.variable.replace(/{{|}}/g, "");
          });
        }
      });
    }

    this.reOrderRetries();
    // validate if generalData and variables exist
  }

  setCampaign(campaign){
    this.campaign = campaign;
  }

  get retriesArray() {    
    return this.retries ? Object.keys(this.retries) : [];
  }
  
  get isLoading() {
    return this.isGetTemplateBody || this.isAddRetry;
  }
  
  get retriesCount() {
    return this.retriesArray.length;
  }
  
  get isMaxRetries() {
    return this.retriesCount >= 10;
  }
  
  saveEditorData({ currentRetry }) {
    currentRetry.isSavingContent = true;
    this.retries[currentRetry.version] = currentRetry;
    
    const { design, variables } = currentRetry.templateEditorStore.exportData();
    currentRetry.tempContent = design?.body?.rows[0]?.columns[0]?.contents[0]?.values?.text || "";
    currentRetry.isSavingContent = false;
    currentRetry.variables = variables;
    
    this.retries[currentRetry.version] = currentRetry;
  }

  setVariablesFromFile({ variables, sources }){
    if (!Array.isArray(variables) || variables.length === 0) {
      return;
    }

    this.mappedVariables = variables;
    this.sources = sources;

    Object.keys(this.retries).forEach((retryVersion) => {
      this.retries[retryVersion].templateEditorStore.removeAllMappedVariables();
      this.retries[retryVersion].templateEditorStore.updateField('sources', sources);

      this.retries[retryVersion].variables = variables
        .filter((field) => field !== "email")
        .map((field) => ({
          name: field,
          variable: field,
          origin: OriginVariables.EVENT,
          isMapped: true,
          source: 'contacts',
          field,
        }));

      this.retries[retryVersion].variables.forEach((variable) => this.retries[retryVersion].templateEditorStore.addVariable(variable));
    });
  }

  async expandEditor({ index }) {
    const retry = this.getOne({ index });
    
    if (retry.isExpanded) {
      this.saveEditorData({ currentRetry: retry });
    } else {
      if (!retry.tempContent) {
        retry.tempContent = await this.fetchRetryTemplate({
          message: retry.message,
          unit: retry.unit,
          value: retry.value,
        });
      }
      retry.templateEditorStore.updateField("text", retry.tempContent);
    }

    retry.isExpanded = !retry.isExpanded;

    this.retries[retry.version] = retry;
  }
  
  async getRetryTemplate({ index }){
    const retrySelected = this.retries[index];
    
    try {
      retrySelected.isGeneratingRetryMessage = true;

      const message = await this.fetchRetryTemplate({
        message: retrySelected.message,
        unit: retrySelected.unit,
        value: retrySelected.value,
      });

     this.retries[retrySelected.version] = {
       ...this.retries[retrySelected.version],
       message,
       tempContent: message,
       isApiContent: true,
       isGeneratingRetryMessage: false,
     };
    } catch (error) {
      this.modalStore.setup({
        modal: "generalMessageModal",
        title: "Opps!",
        message: "Error trying get retry message template"
      });
    }
  }
  
  *fetchRetryTemplate({ message, unit, value }) {
    const response = yield API.graphql({
      query: getCampaignRetryTemplate,
      variables: {
        input: {
          message: message,
          unit: unit,
          value: value,
          customer_id: this.customer,
          campaign_id: this.campaign.id,
        },
      },
      authMode: "AMAZON_COGNITO_USER_POOLS",
    });

    return response.data?.getCampaignRetryTemplate?.body;
  }
  
  updateRetryAttribute({ index, field, value }) {
    const retry = this.getOne({ index });
    retry[field] = value;
    this.retries[retry.version] = retry;
  }
  
  getOne({ index }) {
    return this.retries[index];
  }
  
  *getEmailBody(){
    const actionSelected = this.campaign.metadata.actions.find((a) => a.action === "send_email");
    if (actionSelected) {
      const body = actionSelected.payload.find((item) => item.field === "body");
      if (body) {
        if (body.value) {
          const response = yield this.fetchEmailMessageBody(body.value);
          return response;
        }
        
        return "";
      } 
    }

    return "";
  }
  
  async add() {
    try {      
      if (!this.isMaxRetries) {
        this.isAddRetry = true;

        const message = await this.getEmailBody();
        const newVersion = `v${this.retriesCount + 1}`;

        const action = this.campaign.metadata.actions[0];
        const variables = action.binding.map((binding) => {
          binding.variable = binding.variable.replaceAll("{", "");
          binding.variable = binding.variable.replaceAll("}", "");

          return binding;
        });

        const editor = new TemplateEditorStore();
        variables.forEach((variable) => editor.addVariable(variable));

        this.retries[newVersion] = {
          templateEditorStore: editor,
          version: newVersion,
          unit: "days",
          value: 1,
          tempContent: message,
          message,
          variables
        }
      }
    } catch (error) {

      this.modalStore.setup({
        modal: "generalMessageModal",
        title: "Opps!",
        message: "Error trying add retry"
      });
    } finally {
      this.isAddRetry = false;
    }
  }
  
  remove({ index }){
    delete this.retries[index];
    this.reOrderRetries();
  }
  
  reOrderRetries(){
    const reorderedRetries = {};
    let indexTemp = 1;
    for (const key in this.retries) {
      reorderedRetries[`v${indexTemp}`] = this.retries[key];
      reorderedRetries[`v${indexTemp}`].version = `v${indexTemp}`;
      reorderedRetries[`v${indexTemp}`].isGeneratingRetryMessage = this.retries[key].isGeneratingRetryMessage || false;
      reorderedRetries[`v${indexTemp}`].isExpanded = this.retries[key].isExpanded || false;
      reorderedRetries[`v${indexTemp}`].isSavingContent = this.retries[key].isSavingContent || false;
      reorderedRetries[`v${indexTemp}`].variables = this.retries[key].variables || [];
      reorderedRetries[`v${indexTemp}`].templateEditorStore = new TemplateEditorStore();

      // eslint-disable-next-line no-loop-func
      reorderedRetries[`v${indexTemp}`].variables.forEach((variable) => reorderedRetries[`v${indexTemp}`].templateEditorStore.addVariable(variable));

      indexTemp++;
    }
    
    this.retries = reorderedRetries;
  }
  
  *fetchEmailMessageBody(fileName){
    try {
      this.isGetTemplateBody = true;

      const response = yield API.graphql({
        query: getUrlFile,
        variables: {
          input: {
            customer: this.customer,
            file_name: fileName,
            file_route: "email_templates",
          },
        },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });

      const getUrl = JSON.parse(response.data.getUrlFile?.body);
      const res = yield axios.get(getUrl);
      
      return res.data || "";
    } catch (error) {

      this.modalStore.setup({
        modal: "generalMessageModal",
        title: "Opps!",
        message: "Error trying get body message template"
      });
    } finally {
      this.isGetTemplateBody = false;
    }
  }
}

export default RetryStore;
